import type { ImportRow } from "../dataImport/types";
import { toNumber, toText } from "../dataImport/parseCell";
import type {
  CustomFieldDefinition,
  CustomFieldValues,
  CustomFieldType,
} from "../types";
import {
  findOption,
  toBoolean,
  toConfiguredValues,
  toDay,
  toExcelDate,
  toLabels,
} from "./cells";
import type { SheetColumn, SheetColumnKind } from "./types";

const KIND_BY_TYPE: Record<CustomFieldType, SheetColumnKind> = {
  text: "text",
  long_text: "long_text",
  number: "number",
  currency: "currency",
  date: "date",
  select: "choice",
  multi_select: "choices",
  boolean: "boolean",
  rating: "rating",
  url: "text",
};

/** Import key of a custom field column, apart from the built-in fields. */
const columnKey = (definition: CustomFieldDefinition) =>
  `custom.${definition.key}`;

/** One column per custom field created in the Settings. */
export const customFieldColumns = <
  RecordType extends { custom_fields?: CustomFieldValues },
>(
  definitions: CustomFieldDefinition[],
  booleanLabels: { yes: string; no: string },
): SheetColumn<RecordType>[] =>
  definitions.map((definition) => ({
    key: columnKey(definition),
    header: definition.label,
    group: "custom",
    kind: KIND_BY_TYPE[definition.type],
    options:
      definition.type === "boolean"
        ? [
            { value: "true", label: booleanLabels.yes },
            { value: "false", label: booleanLabels.no },
          ]
        : definition.options,
    get: (record) => {
      const value = record.custom_fields?.[definition.key];
      if (value == null || value === "") return undefined;
      switch (definition.type) {
        case "select":
        case "multi_select":
          return toLabels(value as string | string[], definition.options ?? []);
        case "date":
          return toExcelDate(String(value));
        case "boolean":
          return value ? booleanLabels.yes : booleanLabels.no;
        default:
          return Array.isArray(value) ? value.join(", ") : value;
      }
    },
  }));

/** Custom field values of an imported row; empty and unreadable cells are left out. */
export const readCustomFields = (
  row: ImportRow,
  definitions: CustomFieldDefinition[],
): CustomFieldValues => {
  const values: CustomFieldValues = {};
  definitions.forEach((definition) => {
    const cell = row[columnKey(definition)];
    const value = readCustomFieldCell(cell, definition);
    if (value !== undefined) values[definition.key] = value;
  });
  return values;
};

const readCustomFieldCell = (
  cell: ImportRow[string],
  definition: CustomFieldDefinition,
) => {
  switch (definition.type) {
    case "number":
    case "currency":
    case "rating":
      return toNumber(cell);
    case "boolean":
      return toBoolean(cell);
    case "select": {
      const text = toText(cell);
      return text
        ? findOption(text, definition.options ?? [])?.value
        : undefined;
    }
    case "multi_select": {
      const values = toConfiguredValues(cell, definition.options ?? []);
      return values.length ? values : undefined;
    }
    case "date":
      return toDay(cell);
    default:
      return toText(cell);
  }
};
